import { Component, OnInit, OnDestroy, ViewEncapsulation } from "@angular/core";
import { CommonModule } from "@angular/common";
import { ActivatedRoute, Router } from "@angular/router";
import { TabsModule } from "primeng/tabs";
import { firstValueFrom, Subscription } from "rxjs";

import { db, Media } from "../../../../core/database/db";

import ScheduleDetailHeader from "../../../../domains/jobs/components/shared/schedule-detail-header/schedule-detail-header.component";
import { ReportStateService } from "./report-state.service";
import { IssuesChartComponent } from "../../../../domains/jobs/components/shared/issues-chart/issues-chart.component";
import { SkeletonModule } from "primeng/skeleton";
import CardReportItem from "./card-report-item/card-report-item.component";
import { JobService } from "../../../../domains/jobs/services/job.service";
import { ExtendedAssertion, ExtendedIssue } from "../../../../domains/reports/models/BQ7ReportResponseDTO";
import { InspectionJobDetail } from "../../../../domains/jobs/models/InspectionJobDetail";
import JobDataService from "../../../../domains/jobs/services/job-data-service";
import { LoggerService } from "../../../../shared/services/logger.service";

interface AreaGroup {
  zoneName: string;
  areaName: string;
  assertions: ExtendedAssertion[];
}

@Component({
  selector: "app-job-view-report",
  standalone: true,
  imports: [
    CommonModule,
    TabsModule,
    ScheduleDetailHeader,
    IssuesChartComponent,
    SkeletonModule,
    CardReportItem
  ],
  templateUrl: "./job-view-report.component.html",
  styleUrls: ["./job-view-report.component.scss"],
  encapsulation: ViewEncapsulation.None,
  host: { class: "job-view-report" }
})
export default class JobViewReportComponent implements OnInit, OnDestroy {
  activeTab = "0";
  streetAddress = "";
  financialCommunityName = "";
  lotBlock = "";

  private activeTabSubscription?: Subscription;

  job?: InspectionJobDetail;
  assertions: ExtendedAssertion[] = [];
  needsAttention: ExtendedAssertion[] = [];
  allIssues: ExtendedIssue[] = [];

  minorCount = 0;
  majorCount = 0;
  missingCount = 0;

  fromCompletedJobs: boolean = false;
  dataLoaded: boolean = false;
  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private reportStateService: ReportStateService,
    private jobService: JobService,
    private jobDataService: JobDataService,
    private logger: LoggerService
  ) {
    const state = this.router.getCurrentNavigation()?.extras?.state;
    if (state) {
      if (state["online"]) {
        this.fromCompletedJobs = true;
      }
    }
  }

  async ngOnInit(): Promise<void> {
    this.activeTabSubscription = this.reportStateService.activeTab$.subscribe(
      activeTab => {
        this.activeTab = activeTab;
      }
    );

    const idFromRoute =
      Number(this.route.snapshot.paramMap.get("jobId")) ||
      Number(this.route.snapshot.queryParamMap.get("id")) ||
      undefined;


    if (!this.fromCompletedJobs) {
      await this.loadJob(idFromRoute);
      await this.loadAssertions();
      this.buildNeedsAttention();
      await this.buildAllIssues();

    } else {
      const reportDataResponse = await firstValueFrom(this.jobService.getJobReport(idFromRoute!));

      this.allIssues = reportDataResponse.issues;
      this.assertions = reportDataResponse.looksGoodWithImages;
      this.streetAddress = reportDataResponse.streetAddress;
      this.financialCommunityName = reportDataResponse.financialCommunityName;
      this.lotBlock = reportDataResponse.lotBlock;
    }

    this.refreshCounts();

    this.dataLoaded = true;
  }

  ngOnDestroy(): void {
    this.activeTabSubscription?.unsubscribe();
  }

  private async loadJob(jobId?: number): Promise<void> {
    if (!jobId) {
      this.streetAddress = "—";
      this.financialCommunityName = "—";
      return;
    }

    this.job = await this.jobDataService.getInspectionJobDetailByJobId(jobId);

    if (!this.job) {
      this.logger.warn("No job found");
      this.streetAddress = "—";
      this.financialCommunityName = "—";
      return;
    }

    this.streetAddress = this.job.streetAddress;
    this.financialCommunityName = this.job.financialCommunityName;
    this.lotBlock = this.job.lotBlock;
  }

  private async loadAssertions(): Promise<void> {
    if (!this.job) {
      return;
    }

    const assertions = await db.assertion.where("lotId").equals(this.job.lotId)
      .toArray();
    const extendedAssertions: ExtendedAssertion[] = [];

    for (const assertion of assertions) {
      const component = await db.component.get(assertion.componentId);
      if (!component) {
        continue;
      }

      const area = await db.area.get(component.areaId);
      if (!area) {
        continue;
      }

      const zone = await db.zone.get(area.zoneId);
      if (!zone) {
        continue;
      }

      const issues = await db.assertionIssue.where("assertionIdentifier").equals(assertion.identifier)
        .and(issue => issue.deleted !== 1)
        .toArray();
      const media = await this.loadAssertionImages(assertion.id!);

      const extendedAssertion: ExtendedAssertion = {
        ...assertion,
        jobId: this.job.id,
        issues,
        media,
        zoneName: zone.name,
        areaName: area.name,
        componentName: component.name,
      };

      extendedAssertions.push(extendedAssertion);
    }

    this.assertions = extendedAssertions;
  }

  private async loadAssertionImages(assertionId: number): Promise<Media[]> {
    try {
      const assertion = await db.assertion.get(assertionId);
      if (!assertion) {
        this.logger.warn("Assertion not found for images");
        return [];
      }

      const mediaRecords = await db.media
        .where("parentIdentifier")
        .equals(assertion.identifier)
        .and(media => media.deleted !== 1)
        .toArray();

      return mediaRecords.sort((a, b) => {
        const getSortValue = (media: Media): number => media.id || 0;

        const aValue = getSortValue(a);
        const bValue = getSortValue(b);

        return bValue - aValue;
      });
    } catch (error) {
      this.logger.warn("Error loading assertion image:", error);
      return [];
    }
  }

  private async loadIssueImages(issueId: number): Promise<Media[]> {
    try {
      const issue = await db.assertionIssue.get(issueId);
      if (!issue) {
        this.logger.warn("Issue not found for images");
        return [];
      }

      const mediaRecords = await db.media
        .where("parentIdentifier")
        .equals(issue.identifier)
        .and(media => media.deleted !== 1)
        .toArray();

      return mediaRecords.sort((a, b) => {
        const getSortValue = (media: Media): number => media.id || 0;

        const aValue = getSortValue(a);
        const bValue = getSortValue(b);

        return bValue - aValue;
      });
    } catch (error) {
      this.logger.error("Error loading issue image:", error);
      return [];
    }
  }

  private buildNeedsAttention(): void {
    this.needsAttention = this.assertions.filter(a => {
      const hasIssues = (a.issues?.length ?? 0) > 0;
      const isNotCompleted = !a.isCcmComplete;
      return hasIssues || isNotCompleted;
    });
  }

  private async buildAllIssues(): Promise<void> {
    const extendedIssues: ExtendedIssue[] = [];

    for (const assertion of this.needsAttention) {
      if (assertion.issues && assertion.issues.length > 0) {
        for (const issue of assertion.issues) {
          const media = issue.id ? await this.loadIssueImages(issue.id) : [];

          const extendedIssue: ExtendedIssue = {
            ...issue,
            assertionText: assertion.text || "",
            zoneName: assertion.zoneName,
            areaName: assertion.areaName,
            componentName: assertion.componentName,
            assertionId: assertion.id!,
            media
          };

          extendedIssues.push(extendedIssue);
        }
      }
    }

    this.allIssues = extendedIssues;
  }

  private refreshCounts(): void {
    this.minorCount = this.allIssues.filter(i => i.severity?.toLowerCase() === "minor").length;
    this.majorCount = this.allIssues.filter(i => i.severity?.toLowerCase() === "major").length;
    this.missingCount = this.allIssues.filter(i => i.severity?.toLowerCase() === "missing").length;
  }

  navigateToAssertion(assertion: ExtendedAssertion): void {
    if (this.fromCompletedJobs) {
      this.router.navigate([
        "/ccm/bq7/job", assertion.jobId,
        "report",
        "assertion", assertion.id
      ], {
        state: { assertion }
      });
    } else {
      if (!this.job || !assertion.id) {
        return;
      }

      this.router.navigate([
        "/ccm/bq7/job", this.job.id,
        "report",
        "assertion", assertion.id
      ]);
    }

  }

  navigateToIssue(issue: ExtendedIssue): void {

    if (this.fromCompletedJobs) {
      this.router.navigate([
        "/ccm/bq7/job", issue.jobId,
        "report",
        "issue", issue.id,
        "assertion", issue.assertionId
      ], {
        state: { issue }
      });
    } else {
      if (!this.job || !issue.id) {
        return;
      }
      this.router.navigate([
        "/ccm/bq7/job", this.job.id,
        "report",
        "issue", issue.id,
        "assertion", issue.assertionId
      ]);
    }

  }

  looksGoodGroupedByArea() {
    const looksGood = this.assertions.filter(a => {
      const hasNoIssues = (a.issues?.length ?? 0) === 0;
      const isComplete = a.isCcmComplete;  // this only needs to look at isCcmComplete because this is a CCM only component
      const hasImages = a.media?.length && a.media.length > 0;
      const hasImagesOrNote = hasImages || !!a.ccmNote; // this only needs to look at ccmNote because this is a CCM only component

      return hasNoIssues && isComplete && hasImagesOrNote;
    });

    const grouped = looksGood.reduce((acc, assertion) => {
      const key = `${assertion.zoneName}-${assertion.areaName}`;
      if (!acc[key]) {
        acc[key] = {
          zoneName: assertion.zoneName,
          areaName: assertion.areaName,
          assertions: [],
        };
      }
      acc[key].assertions.push(assertion);
      return acc;
    }, {} as Record<string, {
      zoneName: string;
      areaName: string;
      assertions: ExtendedAssertion[]
    }>);

    return Object.values(grouped);
  }

  issuesBySeverity() {
    return {
      minor: this.allIssues.filter(i => i.severity?.toLowerCase() === "minor"),
      major: this.allIssues.filter(i => i.severity?.toLowerCase() === "major"),
      missing: this.allIssues.filter(i => i.severity?.toLowerCase() === "missing"),
    };
  }

  get needsAttentionTotal(): number {
    return this.allIssues.length;
  }

  get subtitle(): string {
    const parts = [];
    if (this.streetAddress && this.streetAddress !== "—") {
      parts.push(this.streetAddress);
    }
    if (this.financialCommunityName && this.financialCommunityName !== "—") {
      parts.push(this.financialCommunityName);
    }
    parts.push(`Lot ${this.lotBlock}`);

    return parts.length > 0 ? parts.join(", ") : "—";
  }

  onTabChange(tabValue: string | number): void {
    const tabString = tabValue.toString();
    this.reportStateService.setActiveTab(tabString);
  }

  trackByAreaKey = (_: number, group: AreaGroup) => `${group.zoneName}-${group.areaName}`;
  trackByAssertion = (_: number, a: ExtendedAssertion) => a.id;
  trackByIssue = (_: number, i: ExtendedIssue) => i.id ?? i.text;

  goBack(): void {
    if (window.history.length > 1) {
      window.history.back();
    } else {
      this.router.navigate(["/ccm/schedule"]);
    }
  }
}
